import { type MaybeRef, unref } from "vue";
import type {
  AddAppBinaryBody,
  AddDeviceBody,
  ImportLicensesViaLicenseOrderXmlFileParams,
  SendSupportMessageBody,
  UpdateCompanyProfileBody,
  UpdateCoreDataBody,
  UpdateCreditNoteTemplateForHubBody,
  UpdateDeviceBody,
  UpdateInvoiceTemplateForHubBody,
  UpdateVersionDataBody,
  UploadStoreFileToSetContainerIdBody,
  WebModelGetAppVersion,
  WebModelGetCompanyProfile,
  WebModelGetDevice,
  WebModelGetLicenseImportResult,
} from "~/model/api/pLCnextStoreFrontendAPI.schemas";
import axios, { type AxiosRequestConfig, type AxiosResponse } from "axios";

/**
 * https://github.com/orval-labs/orval/issues/869
 * Multipart requests do not work with swagger and orval to generate the correct
 * multipart/form-data requests. This is a workaround until orval supports multipart/form-data with application/json
 */

export const addDevice = (
  addDeviceBody: MaybeRef<AddDeviceBody>,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<WebModelGetDevice>> => {
  addDeviceBody = unref(addDeviceBody);
  const formData = new FormData();
  if (addDeviceBody.iconFile !== undefined) {
    formData.append(`iconFile`, addDeviceBody.iconFile);
  }
  const jsonBlob = new Blob([JSON.stringify(addDeviceBody.json)], {
    type: "application/json",
  });
  formData.append("json", jsonBlob);

  return axios.post(`/service/api/devices`, formData, options);
};

export const updateDevice = (
  updateDeviceBody: MaybeRef<UpdateDeviceBody>,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<WebModelGetDevice>> => {
  updateDeviceBody = unref(updateDeviceBody);
  const formData = new FormData();
  if (updateDeviceBody.iconFile !== undefined) {
    formData.append(`iconFile`, updateDeviceBody.iconFile);
  }
  const jsonBlob = new Blob([JSON.stringify(updateDeviceBody.json)], {
    type: "application/json",
  });
  formData.append("json", jsonBlob);

  return axios.put(`/service/api/devices`, formData, options);
};

export const sendSupportMessage = (
  sendSupportMessageBody: MaybeRef<SendSupportMessageBody>,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  sendSupportMessageBody = unref(sendSupportMessageBody);
  const formData = new FormData();
  const jsonBlob = new Blob([JSON.stringify(sendSupportMessageBody.json)], {
    type: "application/json",
  });
  if (sendSupportMessageBody.files !== undefined) {
    sendSupportMessageBody.files.forEach((value) =>
      formData.append(`files`, value),
    );
  }
  formData.append("json", jsonBlob);

  return axios.post(`/service/api/messages/support`, formData, options);
};

export const uploadStoreFileToSetContainerId = (
  id: MaybeRef<string>,
  uploadStoreFileToSetContainerIdBody: MaybeRef<UploadStoreFileToSetContainerIdBody>,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  id = unref(id);
  uploadStoreFileToSetContainerIdBody = unref(
    uploadStoreFileToSetContainerIdBody,
  );
  const formData = new FormData();
  if (uploadStoreFileToSetContainerIdBody.racFile !== undefined) {
    formData.append(`racFile`, uploadStoreFileToSetContainerIdBody.racFile);
  }

  return axios.post(
    `/service/api/devices/${id}/licenses/container`,
    formData,
    options,
  );
};

export const updateCompanyProfile = (
  updateCompanyProfileBody: MaybeRef<UpdateCompanyProfileBody>,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<WebModelGetCompanyProfile>> => {
  updateCompanyProfileBody = unref(updateCompanyProfileBody);
  const formData = new FormData();
  if (updateCompanyProfileBody.iconFile !== undefined) {
    formData.append(`iconFile`, updateCompanyProfileBody.iconFile);
  }
  if (updateCompanyProfileBody.bannerFile !== undefined) {
    formData.append(`bannerFile`, updateCompanyProfileBody.bannerFile);
  }
  if (updateCompanyProfileBody.privacyPolicyFile !== undefined) {
    formData.append(
      `privacyPolicyFile`,
      updateCompanyProfileBody.privacyPolicyFile,
    );
  }
  const jsonBlob = new Blob([JSON.stringify(updateCompanyProfileBody.json)], {
    type: "application/json",
  });
  formData.append("json", jsonBlob);

  return axios.put(`/service/api/company-profiles`, formData, options);
};

export const updateCoreData = (
  appId: MaybeRef<number>,
  updateCoreDataBody: MaybeRef<UpdateCoreDataBody>,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<WebModelGetAppVersion>> => {
  appId = unref(appId);
  updateCoreDataBody = unref(updateCoreDataBody);
  const formData = new FormData();
  const jsonBlob = new Blob([JSON.stringify(updateCoreDataBody.json)], {
    type: "application/json",
  });
  if (updateCoreDataBody.file !== undefined) {
    formData.append(`file`, updateCoreDataBody.file);
  }
  formData.append("json", jsonBlob);

  return axios.put(`/service/api/apps/app-id/${appId}`, formData, options);
};

export const updateVersionData = (
  id: MaybeRef<number>,
  updateVersionDataBody: MaybeRef<UpdateVersionDataBody>,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<WebModelGetAppVersion>> => {
  id = unref(id);
  updateVersionDataBody = unref(updateVersionDataBody);
  const formData = new FormData();
  const jsonBlob = new Blob([JSON.stringify(updateVersionDataBody.json)], {
    type: "application/json",
  });
  formData.append("json", jsonBlob);
  if (updateVersionDataBody.changelogFile !== undefined) {
    formData.append(`changelogFile`, updateVersionDataBody.changelogFile);
  }
  if (updateVersionDataBody.pictureVideoUsecaseFiles !== undefined) {
    updateVersionDataBody.pictureVideoUsecaseFiles.forEach((value) =>
      formData.append(`pictureVideoUsecaseFiles`, value),
    );
  }
  if (updateVersionDataBody.technicalDocumentationFiles !== undefined) {
    updateVersionDataBody.technicalDocumentationFiles.forEach((value) =>
      formData.append(`technicalDocumentationFiles`, value),
    );
  }

  return axios.put(`/service/api/apps/id/${id}`, formData, options);
};

export const addAppBinary = (
  id: MaybeRef<number>,
  addAppBinaryBody?: MaybeRef<AddAppBinaryBody>,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<WebModelGetAppVersion>> => {
  id = unref(id);
  addAppBinaryBody = unref(addAppBinaryBody);
  const formData = new FormData();
  if (addAppBinaryBody?.json !== undefined) {
    const jsonBlob = new Blob([JSON.stringify(addAppBinaryBody.json)], {
      type: "application/json",
    });
    formData.append(`json`, jsonBlob);
  }
  if (addAppBinaryBody?.appFile !== undefined) {
    formData.append(`appFile`, addAppBinaryBody.appFile);
  }

  return axios.post(`/service/api/apps/id/${id}/binaries`, formData, options);
};

export const updateCreditNoteTemplateForHub = (
  hubid: MaybeRef<string>,
  updateCreditNoteTemplateForHubBody: MaybeRef<UpdateCreditNoteTemplateForHubBody>,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  hubid = unref(hubid);
  updateCreditNoteTemplateForHubBody = unref(
    updateCreditNoteTemplateForHubBody,
  );
  const formData = new FormData();
  if (updateCreditNoteTemplateForHubBody.file !== undefined) {
    formData.append(`file`, updateCreditNoteTemplateForHubBody.file);
  }

  return axios.put(
    `/service/api/hubs/${hubid}/template/creditnote`,
    formData,
    options,
  );
};

export const updateInvoiceTemplateForHub = (
  hubid: MaybeRef<string>,
  updateInvoiceTemplateForHubBody: MaybeRef<UpdateInvoiceTemplateForHubBody>,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  hubid = unref(hubid);
  updateInvoiceTemplateForHubBody = unref(updateInvoiceTemplateForHubBody);
  const formData = new FormData();
  if (updateInvoiceTemplateForHubBody.file !== undefined) {
    formData.append(`file`, updateInvoiceTemplateForHubBody.file);
  }
  return axios.put(
    `/service/api/hubs/${hubid}/template/invoice`,
    formData,
    options,
  );
};

export const importLicensesViaLicenseOrderXmlFile = (
  importLicensesViaLicenseOrderXmlFileBody: MaybeRef<Blob>,
  params?: MaybeRef<ImportLicensesViaLicenseOrderXmlFileParams>,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<WebModelGetLicenseImportResult>> => {
  importLicensesViaLicenseOrderXmlFileBody = unref(
    importLicensesViaLicenseOrderXmlFileBody,
  );
  params = unref(params);

  return axios.post(
    `/service/api/app-licenses/create`,
    importLicensesViaLicenseOrderXmlFileBody,
    {
      ...options,
      headers: {
        "Content-Type": "application/xml",
        ...options?.headers,
      },
      params: { ...unref(params), ...options?.params },
    },
  );
};
